/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Shared utilities for parsing and formatting issues from markdown
 * Used by both the backend issue manager tool and the product app
 */

import * as fs from 'fs';
import * as path from 'path';
import { 
  Issue, 
  IssueType, 
  IssueStatus, 
  EstimateSize,
  ISSUE_TYPES,
  ISSUE_STATUSES,
  ESTIMATE_SIZES
} from '../types/issues';

// Re-export types and constants for backward compatibility
export type { Issue, IssueType, IssueStatus, EstimateSize };
export { ISSUE_TYPES, ISSUE_STATUSES, ESTIMATE_SIZES };

/**
 * Normalizes line endings in content by converting CRLF (\r\n) to LF (\n)
 * and removing any standalone CR (\r) characters.
 * This ensures consistent parsing regardless of whether files were created
 * on Windows (CRLF) or Unix/Mac (LF).
 */
export function normalizeLineEndings(content: string): string {
  return content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

/**
 * Parses issues from markdown content in the format used in issues.md
 * This function preserves the exact original formatting of multi-line content
 */
export function parseIssuesFromMarkdown(content: string): Issue[] {
  const issues: Issue[] = [];
  // Normalize line endings to handle Windows CRLF files
  const lines = normalizeLineEndings(content).split('\n');
  
  let currentIssue: Partial<Issue> | null = null;
  let inIssueSection = false;
  let currentField: string | null = null;
  let currentFieldValue: string = '';
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();
    
    // Check for issue header
    const issueMatch = line.match(/^### ([A-Z]+-\d+): (.+)$/);
    if (issueMatch) {
      // Save any pending field value
      if (currentIssue && currentField && currentFieldValue.length > 0) {
        setFieldValue(currentIssue, currentField, currentFieldValue);
      }
      
      // Save previous issue if exists
      if (currentIssue && currentIssue.id) {
        issues.push(currentIssue as Issue);
      }
      
      // Start new issue
      const [, id, title] = issueMatch;
      currentIssue = {
        id: `${id}: ${title}`,
        title: title
      };
      inIssueSection = true;
      currentField = null;
      currentFieldValue = '';
      continue;
    }
    
    // Check for section end (new ## section or --- separator)
    // BUT only if we're NOT currently collecting a multi-line field
    // This prevents markdown headers within field content from ending the issue
    if (!currentField && (trimmedLine.startsWith('## ') || trimmedLine.startsWith('---'))) {
      // Save any pending field value (shouldn't happen since currentField is null)
      if (currentIssue && currentField && currentFieldValue.length > 0) {
        setFieldValue(currentIssue, currentField, currentFieldValue);
      }
      
      // Save current issue
      if (currentIssue && currentIssue.id) {
        issues.push(currentIssue as Issue);
      }
      currentIssue = null;
      inIssueSection = false;
      currentField = null;
      currentFieldValue = '';
      continue;
    }
    
    if (!currentIssue || !inIssueSection) continue;
    
    // Parse issue fields - must match pattern: - **FieldName:** value
    const fieldMatch = line.match(/^- \*\*([^:]+):\*\* (.*)$/);
    if (fieldMatch) {
      // Save any pending field value before starting new field
      if (currentField && currentFieldValue.length > 0) {
        setFieldValue(currentIssue, currentField, currentFieldValue);
        currentField = null;
        currentFieldValue = '';
      }
      
      const [, fieldName, fieldValue] = fieldMatch;
      currentField = fieldName;
      currentFieldValue = fieldValue;
      continue;
    }
    
    // Continue collecting multi-line field content
    if (currentField) {
      // Stop collecting if we hit:
      // 1. A new issue (### ISSUE-ID:)
      // 2. A section header (## ...) BUT only if we haven't started collecting content yet
      //    - If currentFieldValue is empty (just the field name line), ## is a section end
      //    - If currentFieldValue has content, ## might be a markdown header within content
      // 3. A standalone separator (---)
      
      const isNewIssue = /^### ([A-Z]+-\d+): /.test(line);
      const isSectionHeader = trimmedLine.startsWith('## ');
      const isSeparator = trimmedLine === '---';
      
      // Section headers should end the current field ONLY if we haven't collected any content yet
      // This allows "## Summary" within code logs while stopping at "## Task Issues" between issues
      if (isSectionHeader && currentFieldValue.trim() === '') {
        // Empty field followed by section header - end the issue
        if (currentFieldValue.length > 0) {
          setFieldValue(currentIssue, currentField, currentFieldValue);
        }
        
        if (currentIssue && currentIssue.id) {
          issues.push(currentIssue as Issue);
        }
        
        currentIssue = null;
        inIssueSection = false;
        currentField = null;
        currentFieldValue = '';
        continue;
      }
      
      if (isNewIssue) {
        // Save the current field value before starting the new issue
        if (currentFieldValue.length > 0) {
          setFieldValue(currentIssue, currentField, currentFieldValue);
        }
        
        // Save the current issue
        if (currentIssue && currentIssue.id) {
          issues.push(currentIssue as Issue);
        }
        
        // Parse the new issue header
        const issueMatch = line.match(/^### ([A-Z]+-\d+): (.+)$/);
        if (issueMatch) {
          const [, id, title] = issueMatch;
          currentIssue = {
            id: `${id}: ${title}`,
            title: title
          };
          inIssueSection = true;
          currentField = null;
          currentFieldValue = '';
        }
        continue;
      }
      
      if (isSeparator) {
        // Save the current field value
        if (currentFieldValue.length > 0) {
          setFieldValue(currentIssue, currentField, currentFieldValue);
        }
        
        // Save the current issue
        if (currentIssue && currentIssue.id) {
          issues.push(currentIssue as Issue);
        }
        
        currentIssue = null;
        inIssueSection = false;
        currentField = null;
        currentFieldValue = '';
        continue;
      }
      
      // Add the line to the current field value, preserving original formatting
      if (currentFieldValue.length > 0) {
        currentFieldValue += '\n' + line;
      } else {
        currentFieldValue = line;
      }
      continue;
    }
  }
  
  // Save any pending field value
  if (currentIssue && currentField && currentFieldValue.length > 0) {
    setFieldValue(currentIssue, currentField, currentFieldValue);
  }
  
  // Save last issue
  if (currentIssue && currentIssue.id) {
    issues.push(currentIssue as Issue);
  }
  
  return issues;
}

/**
 * Sets a field value on an issue, handling different field types appropriately
 */
function setFieldValue(issue: Partial<Issue>, fieldName: string, fieldValue: string) {
  // Trim only single-line field values, preserve multi-line formatting for Description and Plan
  const trimmedValue = fieldValue.trim();
  
  switch (fieldName) {
    case 'Type':
      issue.type = trimmedValue as IssueType;
      break;
    case 'Status':
      issue.status = trimmedValue as IssueStatus;
      break;
    case 'Description':
      // Preserve original formatting for descriptions
      issue.description = fieldValue;
      break;
    case 'Created':
      issue.createdAt = trimmedValue;
      break;
    case 'Updated':
      issue.updatedAt = trimmedValue;
      break;
    case 'Assigned To':
      issue.assignedTo = trimmedValue || undefined;
      break;
    case 'Estimate':
      issue.estimate = trimmedValue as EstimateSize;
      break;
    case 'Effort':
      issue.effort = trimmedValue ? parseInt(trimmedValue) : 0;
      break;
    case 'Links':
      issue.links = trimmedValue ? trimmedValue.split(',').map(s => s.trim()).filter(s => s) : [];
      break;
    case 'Plan':
      // Preserve original formatting for plans
      issue.plan = fieldValue || undefined;
      break;
    case 'Labels':
      issue.labels = trimmedValue ? trimmedValue.split(',').map(s => s.trim()).filter(s => s) : [];
      break;
    case 'Version':
      issue.version = trimmedValue || undefined;
      break;
    case 'Product':
      issue.product = trimmedValue || undefined;
      break;
    case 'Code Log':
      // Preserve original formatting for code logs
      issue.codeLog = fieldValue || undefined;
      break;
  }
}

/**
 * Formats a single issue as markdown, preserving the exact format used in issues.md
 */
export function formatIssueAsMarkdown(issue: Issue): string {
  const idParts = issue.id.split(': ');
  const issueNumber = idParts[0];
  const title = idParts[1] || issue.title;

  let markdown = `### ${issueNumber}: ${title}\n`;
  markdown += `- **Type:** ${issue.type}\n`;
  markdown += `- **Status:** ${issue.status}\n`;
  
  // Handle description formatting to match existing format exactly
  markdown += `- **Description:** ${issue.description}\n`;
  
  markdown += `- **Created:** ${issue.createdAt}\n`;
  markdown += `- **Updated:** ${issue.updatedAt}\n`;
  markdown += `- **Assigned To:** ${issue.assignedTo || ''}\n`;
  markdown += `- **Estimate:** ${issue.estimate || ''}\n`;
  markdown += `- **Effort:** ${issue.effort || 0}\n`;
  markdown += `- **Links:** ${issue.links?.join(', ') || ''}\n`;
  
  // Handle plan formatting to match existing format exactly
  if (issue.plan && issue.plan.trim()) {
    markdown += `- **Plan:** \n${issue.plan}\n`;
  } else {
    markdown += `- **Plan:** \n`;
  }
  
  markdown += `- **Labels:** ${issue.labels?.join(', ') || ''}\n`;
  markdown += `- **Version:** ${issue.version || ''}\n`;
  markdown += `- **Product:** ${issue.product || ''}\n`;
  
  // Handle code log formatting to match existing format exactly
  if (issue.codeLog && issue.codeLog.trim()) {
    markdown += `- **Code Log:** \n${issue.codeLog}\n`;
  } else {
    markdown += `- **Code Log:** \n`;
  }
  
  return markdown;
}

/**
 * Generates the complete markdown content from an array of issues
 * This preserves the exact structure and formatting of issues.md
 */
export function generateMarkdownFromIssues(issues: Issue[]): string {
  const header = `# Development Issues

This file contains all development issues for the SkillShop project, organized by type and status.

`;

  const footer = `
---

## Issue Status Legend

- **Not Started**: Issue has not begun development
- **In Progress**: Issue is actively being worked on
- **Ready For Review**: Issue implementation is finished and ready for review
- **Completed**: Issue implementation is approved and merged 
- **Blocked**: Issue cannot proceed due to dependencies or obstacles
- **On Hold**: Issue development has been paused

## Estimate Sizes

- **XS**: Extra Small (1-2 hours)
- **S**: Small (2-4 hours)
- **M**: Medium (4-8 hours)
- **L**: Large (1-2 days)
- **XL**: Extra Large (2-5 days)
- **XXL**: Extra Extra Large (1+ weeks)`;

  // Group issues by type
  const issuesByType = {
    Bug: issues.filter(i => i.type === 'Bug'),
    Task: issues.filter(i => i.type === 'Task'),
    Feature: issues.filter(i => i.type === 'Feature'),
    Release: issues.filter(i => i.type === 'Release')
  };

  let content = header;

  // Generate sections
  for (const [type, typeIssues] of Object.entries(issuesByType)) {
    content += `## ${type} Issues\n\n`;
    
    if (typeIssues.length === 0) {
      content += `### No ${type} issues currently tracked\n\n`;
    } else {
      for (const issue of typeIssues) {
        content += formatIssueAsMarkdown(issue);
        content += '\n';
      }
    }
  }

  content += footer;
  return content;
}

/**
 * Generates next issue ID based on type and existing issues
 */
export function generateNextIssueId(type: IssueType, existingIssues: Issue[]): string {
  const typeCode = type.toUpperCase();
  const existingIds = existingIssues
    .filter(issue => issue.id.startsWith(typeCode))
    .map(issue => {
      const match = issue.id.match(new RegExp(`${typeCode}-(\\d+)`));
      return match ? parseInt(match[1]) : 0;
    });
  
  const nextNumber = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1;
  return `${typeCode}-${nextNumber.toString().padStart(3, '0')}`;
}

/**
 * Updates a single issue in an array of issues without affecting others
 * This is useful for targeted updates that don't require regenerating the entire file
 */
export function updateIssueInArray(issues: Issue[], issueId: string, updates: Partial<Issue>): Issue[] {
  const issueIndex = issues.findIndex(issue => issue.id.startsWith(issueId) || issue.id === issueId);
  if (issueIndex === -1) {
    throw new Error(`Issue with ID ${issueId} not found`);
  }
  
  const updatedIssues = [...issues];
  updatedIssues[issueIndex] = {
    ...updatedIssues[issueIndex],
    ...updates,
    id: updatedIssues[issueIndex].id, // Preserve original ID
    createdAt: updatedIssues[issueIndex].createdAt, // Preserve creation date
    updatedAt: new Date().toISOString() // Update timestamp
  };
  
  return updatedIssues;
}

/**
 * Adds a new issue to an array of issues
 */
export function addIssueToArray(issues: Issue[], newIssue: Omit<Issue, 'id' | 'createdAt' | 'updatedAt'>): Issue[] {
  const issueId = generateNextIssueId(newIssue.type, issues);
  const now = new Date().toISOString();
  
  const issue: Issue = {
    ...newIssue,
    id: `${issueId}: ${newIssue.title}`,
    createdAt: now,
    updatedAt: now
  };
  
  return [...issues, issue];
}

/**
 * Deletes an issue from an array of issues
 * Returns the updated array and the deleted issue
 */
export function deleteIssueFromArray(issues: Issue[], issueId: string): { issues: Issue[]; deleted: Issue } {
  const issueIndex = issues.findIndex(issue => issue.id.startsWith(issueId) || issue.id === issueId);
  if (issueIndex === -1) {
    throw new Error(`Issue with ID ${issueId} not found`);
  }
  
  const deleted = issues[issueIndex];
  const updatedIssues = [...issues.slice(0, issueIndex), ...issues.slice(issueIndex + 1)];
  
  return { issues: updatedIssues, deleted };
}


//
// issues.md file utilities
//

/**
 * Gets the path to the issues.md file
 */
function getIssuesFilePath(): string {
  return path.join(process.cwd(), 'data', 'product', 'issues.md');
}

/**
 * Reads and parses the issues.md file
 */
export function readIssuesFile(): Issue[] {
  try {
    const filePath = getIssuesFilePath();
    const content = fs.readFileSync(filePath, 'utf8');
    return parseIssuesFromMarkdown(content);
  } catch (error) {
    throw new Error(`Failed to read issues file: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Writes issues array back to the issues.md file
 */
export function writeIssuesFile(issues: Issue[]): void {
  try {
    const content = generateMarkdownFromIssues(issues);
    const filePath = getIssuesFilePath();
    fs.writeFileSync(filePath, content, 'utf8');
  } catch (error) {
    throw new Error(`Failed to write issues file: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Updates the status of a specific issue by ID
 * This is a simple, direct file operation that doesn't require AI tools
 */
export function updateIssueStatus(issueId: string, newStatus: IssueStatus): Issue {
  const issues = readIssuesFile();
  
  // Find the issue by ID (handle both full ID format and just the issue number)
  const issue = issues.find(i => 
    i.id.startsWith(issueId) || 
    i.id === issueId ||
    i.id.includes(issueId)
  );
  
  if (!issue) {
    throw new Error(`Issue with ID '${issueId}' not found`);
  }
  
  // Update the issue status
  const updatedIssues = updateIssueInArray(issues, issue.id, { status: newStatus });
  
  // Write back to file
  writeIssuesFile(updatedIssues);
  
  // Return the updated issue
  const updatedIssue = updatedIssues.find(i => i.id === issue.id);
  if (!updatedIssue) {
    throw new Error('Failed to find updated issue');
  }
  
  return updatedIssue;
}

/**
 * Updates multiple fields of a specific issue by ID
 */
export function updateIssueFields(issueId: string, updates: Partial<Issue>): Issue {
  const issues = readIssuesFile();
  
  // Find the issue by ID
  const issue = issues.find(i => 
    i.id.startsWith(issueId) || 
    i.id === issueId ||
    i.id.includes(issueId)
  );
  
  if (!issue) {
    throw new Error(`Issue with ID '${issueId}' not found`);
  }
  
  // Update the issue
  const updatedIssues = updateIssueInArray(issues, issue.id, updates);
  
  // Write back to file
  writeIssuesFile(updatedIssues);
  
  // Return the updated issue
  const updatedIssue = updatedIssues.find(i => i.id === issue.id);
  if (!updatedIssue) {
    throw new Error('Failed to find updated issue');
  }
  
  return updatedIssue;
}

/**
 * Gets a specific issue by ID
 */
export function getIssueById(issueId: string): Issue | null {
  const issues = readIssuesFile();
  
  return issues.find(i => 
    i.id.startsWith(issueId) || 
    i.id === issueId ||
    i.id.includes(issueId)
  ) || null;
}

/**
 * Gets all issues
 */
export function getAllIssues(): Issue[] {
  return readIssuesFile();
}
